import React, {
    FC,
    useCallback,
} from 'react';

import { Can } from '@casl/react';
import { Button } from 'antd';
import { observer } from 'mobx-react';
import classNames from 'classnames';

import NewPolicyPoP from './NewPolicyPop';
import ability, { SpecterXRules } from '../../../../config/ability';
import DiscardNewPolicyModal, { useDiscardModal } from '../DiscardNewPolicyModal';
import { useStores } from '../../../hooks';
import { COLORS } from '../../../../theme';
import styles from './PolicyEditorSidebar.module.scss';

interface PolicyEditorSidebarProps {
    title: string;
}

const PolicyEditorSidebar: FC<PolicyEditorSidebarProps> = observer(({ title }) => {
    const { policyStore } = useStores();

    const {
        selectedPolicy,
        selectPolicyForEditing,
        policiesListToDropdown,
        hasUnsavedChanges,
    } = policyStore;

    const {
        showDiscardModal,
        openDiscardModal,
        onFinish,
    } = useDiscardModal();

    const onSelectPolicy = (policyId: string): void => {
        const isCreatingNewPolicy = selectedPolicy?.id === '';
        const hasChanges = hasUnsavedChanges();

        // Warn if either creating a new policy or has unsaved changes
        if (isCreatingNewPolicy || hasChanges) {
            openDiscardModal(selectPolicyForEditing, policyId);
        } else {
            selectPolicyForEditing(policyId);
        }
    };

    const onDiscardModalFinish = useCallback(onFinish, []);

    return (
        <>
            <DiscardNewPolicyModal
                isVisible={showDiscardModal}
                onFinish={onDiscardModalFinish}
            />
            <div className={styles['policy-list-container']}>
                <div className={styles['policy-title-container']}>
                    <div className={styles['policy-title']}>{title}</div>
                    <Can I={SpecterXRules.SpecterxEditPolicy} a="ALL" ability={ability}>
                        <NewPolicyPoP openDiscardModal={openDiscardModal} />
                    </Can>
                </div>
                {policiesListToDropdown?.map(({ value, label }) => {
                    const isActive = value === selectedPolicy?.id;
                    return (
                        <Button
                            data-testid={`policyEditor_policyItem_${value}`}
                            onClick={() => onSelectPolicy(String(value))}
                            className={classNames(
                                styles['policy-list-item'],
                                { [styles['policy-list-item-active']]: isActive },
                            )}
                            key={value}
                            block
                            type="link"
                            title={label || String(value)}
                            onMouseEnter={(e) => {
                                const bg = isActive ? COLORS.ctaHover : COLORS.ctaDisabled;
                                e.currentTarget.style.setProperty('background-color', bg, 'important');
                                e.currentTarget.style.setProperty('color', COLORS.cta, 'important');
                            }}
                            onMouseLeave={(e) => {
                                e.currentTarget.style.removeProperty('background-color');
                                e.currentTarget.style.removeProperty('color');
                            }}
                        >
                            {label || value}
                        </Button>
                    );
                })}
            </div>
        </>
    );
});

export default PolicyEditorSidebar;
